import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Group as TeamIcon,
  AssignmentTurnedIn as ValidationIcon,
} from '@mui/icons-material';

const ValidationSummary = ({ data }) => {
  const validUsers = data?.valid_users || [];
  const validationIssues = data?.validation_issues || [];
  const uniqueEmails = new Set(validUsers.map(user => user.email));
  const uniqueTeams = new Set(validUsers.flatMap(user => user.teams));
  const uniqueTeamGroups = new Set(validUsers.flatMap(user => 
    Array.isArray(user.teamGroups) ? user.teamGroups : (user.teamGroups ? [user.teamGroups] : [])
  ));

  return (
    <Box className="mb-3">
      <Typography variant="subtitle1" className="mb-2">
        Validation Summary
      </Typography>
      <List dense>
        <ListItem>
          <ListItemIcon>
            <EmailIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary={`${uniqueEmails.size} unique email addresses verified`}
            secondary="No duplicate emails found in valid users"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <TeamIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary={`${uniqueTeams.size} teams and ${uniqueTeamGroups.size} team groups verified`}
            secondary="All teams and team groups exist in the system"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <ValidationIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary={`${validUsers.length} users passed validation`}
            secondary={`${validationIssues.length} users have validation issues`}
          />
        </ListItem>
      </List>
    </Box>
  );
};

const BulkUserUpload = ({ open, onClose, data, onProceed, loading }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const renderUserTable = (users, type) => (
    <TableContainer component={Paper} variant="outlined" className="my-2">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Teams</TableCell>
            <TableCell>Team Groups</TableCell>
            {type === 'validation' && <TableCell>Issue</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user, idx) => (
            <TableRow key={idx}>
              <TableCell>{user.email}</TableCell>
              <TableCell>{`${user.firstName || ''} ${user.lastName || ''}`}</TableCell>
              <TableCell>{user.role}</TableCell>
              <TableCell>{user.department}</TableCell>
              <TableCell>
                {Array.isArray(user.teams) ? user.teams.join(', ') : user.teams}
              </TableCell>
              <TableCell>
                {Array.isArray(user.teamGroups) 
                  ? user.teamGroups.join(', ') 
                  : (user.teamGroups || '')}
              </TableCell>
              {type === 'validation' && (
                <TableCell>{user.issue}</TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderSection = (title, content, icon, count, type) => (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          bgcolor: 'background.default'
        }}
        onClick={() => setExpandedSection(expandedSection === type ? null : type)}
      >
        {icon}
        <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
          {title}
        </Typography>
        <Chip label={count} color={type === 'valid' ? 'success' : 'error'} />
        <IconButton size="small">
          {expandedSection === type ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expandedSection === type}>
        <Box sx={{ p: 2 }}>
          {content}
        </Box>
      </Collapse>
    </Paper>
  );

  const validUsers = data?.valid_users || [];
  const validationIssues = data?.validation_issues || [];
  const canProceed = validUsers.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Verify User Upload</DialogTitle>
      <DialogContent>
        <ValidationSummary data={data} />
        {validationIssues.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some users have validation issues and will be skipped
          </Alert>
        )}
        
        {renderSection(
          'Valid Users',
          renderUserTable(validUsers, 'valid'),
          <CheckIcon color="success" />,
          validUsers.length,
          'valid'
        )}

        {validationIssues.length > 0 && renderSection(
          'Users with Issues',
          renderUserTable(validationIssues, 'validation'),
          <ErrorIcon color="error" />,
          validationIssues.length,
          'validation'
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {canProceed 
              ? `Ready to create ${validUsers.length} users`
              : 'Please fix validation issues before proceeding'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onProceed}
          disabled={!canProceed || loading}
        >
          {loading ? 'Processing...' : 'Proceed with Valid Users'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUserUpload;